import React from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { H1, Text, H2 } from '../components/ui/Typography';
import { Card } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const InternationalLanding = () => {
  const { lang } = useParams();

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative pt-12 pb-24 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-50 rounded-full blur-3xl opacity-50 -z-10"></div>
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-widest mb-4">
              <SafeIcon name="Globe" className="text-zinc-400" />
              <span>Grensoverschrijdende Expertise</span>
            </div>
            <H1 className="text-gradient leading-tight">
              Internationale nalatenschap en expat-situaties
            </H1>
            <Text className="max-w-2xl mx-auto text-lg md:text-xl">
              Wonen in het buitenland, buitenlands vastgoed of een internationale vermogensstructuur? Ontdek met een gratis scan welke grensoverschrijdende aandachtspunten voor uw nalatenschap spelen.
            </Text>
            <div className="flex flex-col items-center gap-6 pt-4">
              <Link to={`/${lang}/scan`}>
                <Button className="px-12 py-5 text-base shadow-xl">Start gratis scan</Button>
              </Link>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">
                Gespecialiseerde internationale analyse
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Waarom standaard niet genoeg is */}
      <section className="py-24 bg-zinc-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <H2 className="mb-6 text-2xl md:text-3xl">Navigeren door internationale complexiteit</H2>
          <Text className="text-slate-600">
            Zodra er landsgrenzen in het spel zijn, volstaat de standaard Nederlandse wetgeving niet meer. Verschillende landen kunnen aanspraak maken op hetzelfde vermogen, en de EU Erfrechtverordening vraagt om een bewuste keuze van toepasselijk recht om ongewenste verrassingen te voorkomen.
          </Text>
        </div>
      </section>

      {/* 3 Belangrijkste Risico's */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                title: 'Conflicterende regels', 
                desc: 'Verschillende landen kunnen verschillende regels toepassen op dezelfde nalatenschap, wat leidt tot juridische chaos.',
                icon: 'Shield'
              },
              { 
                title: 'Buitenlands vastgoed', 
                desc: 'Buitenlands vastgoed vraagt vaak om extra voorbereiding en specifieke clausules in uw testament.',
                icon: 'Home'
              },
              { 
                title: 'Fiscale verrassingen', 
                desc: 'Zonder duidelijke keuzes kunnen fiscale verrassingen of dubbele belastingheffing over de grens ontstaan.',
                icon: 'AlertCircle'
              }
            ].map((risk, i) => (
              <Card key={i} className="p-8 border-zinc-100 shadow-sm" hover={true}>
                <div className="w-12 h-12 bg-zinc-50 rounded-xl flex items-center justify-center mb-6 text-navy-900">
                  <SafeIcon name={risk.icon} className="text-xl" />
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-3">{risk.title}</h3>
                <Text subtle className="text-sm leading-relaxed">{risk.desc}</Text>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Wat Testamentair oplevert */}
      <section className="py-24 bg-zinc-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-navy-900 mb-6">Wat u ontvangt</h2>
              <Text className="mb-8">
                Na de gratis scan ontvangt u een strategisch dossier met de belangrijkste internationale, fiscale en juridische aandachtspunten voor uw situatie.
              </Text>
              <ul className="space-y-4">
                {[
                  'Inzicht in mogelijke internationale conflictpunten',
                  'Aandachtspunten rond buitenlands vermogen en toepasselijk recht',
                  'Concrete vervolgstappen voor het gesprek met uw notaris'
                ].map((point, i) => (
                  <li key={i} className="flex items-center gap-3 text-zinc-900 font-semibold text-sm">
                    <div className="w-6 h-6 rounded-full bg-navy-900 flex items-center justify-center text-white shrink-0">
                      <SafeIcon name="Check" className="text-[10px]" />
                    </div>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <Card className="p-10 border-none shadow-2xl bg-white rounded-[2.5rem]">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 rounded bg-navy-900 flex items-center justify-center text-white">
                    <SafeIcon name="Globe" className="text-sm" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Internationaal Rapport</span>
                </div>
                <div className="space-y-6">
                  <div className="pt-4 border-t border-slate-50">
                    <div className="text-[10px] font-bold text-slate-400 uppercase mb-2 tracking-widest text-center">Jurisdictie Risico</div>
                    <div className="text-center">
                      <span className="text-3xl font-black text-navy-900">HOOG</span>
                    </div>
                  </div>
                  <div className="h-2 w-full bg-slate-50 rounded"></div>
                  <div className="h-2 w-5/6 bg-slate-50 rounded"></div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Zo werkt het */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center mb-16">
          <h2 className="text-3xl font-bold text-navy-900 mb-4">Internationale intake</h2>
          <Text className="max-w-2xl mx-auto">Wij brengen uw grensoverschrijdende situatie in kaart voor een efficiënt notarisbezoek.</Text>
        </div>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { step: '1. Scan', desc: 'Breng uw internationale bezittingen en woonlanden in kaart via onze intake.' },
              { step: '2. Toetsing', desc: 'Toetsing aan de EU Erfrechtverordening en relevante belastingverdragen.' },
              { step: '3. Dossier', desc: 'Een helder dossier met de noodzakelijke rechtskeuzes voor de notaris.' }
            ].map((s, i) => (
              <div key={i} className="space-y-4">
                <div className="text-navy-900 font-bold text-sm uppercase tracking-widest">{s.step}</div>
                <Text subtle className="text-sm">{s.desc}</Text>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-navy-950 text-white text-center rounded-t-[3rem] relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint-pattern opacity-[0.03]"></div>
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <h2 className="text-3xl md:text-4xl font-semibold mb-6 tracking-tight">Voorkom verrassingen over de grens</h2>
          <p className="text-slate-400 text-lg mb-10">
            Internationale nalatenschappen vragen om gespecialiseerde voorbereiding. Start de gratis scan en ontdek welke stappen passen bij uw situatie.
          </p>
          <Link to={`/${lang}/scan`}>
            <Button variant="white" className="px-12 py-5 text-base"> Start gratis scan </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InternationalLanding;